import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "../../ui/Button";
import Select from "../../ui/Select";
import StatusBadge from "../../ui/StatusBadge";
import { colors, spacing, typography } from "../../ui/tokens";
import { getRiskMatrixConfig } from "../../api/riskMatrixConfig.api";
import { getRiskRatingDefinitions } from "../../api/riskRatingDefinitions.api";
import { getImpactThemeLevels, listImpactThemes } from "../../api/impactThemes.api";
import {
  DEFAULT_RISK_REVIEW_TEMPLATE,
  getRiskReviewTemplate,
} from "../../api/riskReviewTemplate.api";
import AssessmentReviewModal from "./AssessmentReviewModal";
import {
  ensureRichTextHtml,
  isRichTextEmpty,
  replaceTemplatePlaceholders,
  richTextFromPlainText,
  richTextToPlainText,
  sanitizeRichTextHtml,
} from "../../utils/riskReviewTemplate";
import {
  bandToColor,
  getCellScore,
  normalizeMatrixConfig,
  scoreToBand,
} from "../../utils/riskMatrixLogic";
import RiskMatrixSelector from "./RiskMatrixSelector";
import CollapsibleSection from "../../ui/CollapsibleSection";

const toLevel = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.round(parsed);
  if (rounded < 1 || rounded > 5) return null;
  return rounded;
};

const toId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const toMapByLevel = (entries) => {
  const map = new Map();
  (entries || []).forEach((entry) => {
    const level = toLevel(entry?.level);
    if (!level) return;
    map.set(level, {
      label: String(entry?.label || "").trim(),
      definition: String(entry?.definition || "").trim(),
    });
  });
  return map;
};

const formatBandLabel = (band) => {
  const value = String(band || "").trim();
  if (!value) return "Unrated";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const formatReviewLevelLabel = (levelMap, level) => {
  if (level == null) return "-";
  const label = String(levelMap?.get(level)?.label || "").trim();
  return label || String(level);
};

const formatReviewScore = (score) => {
  const parsed = Number(score);
  return Number.isFinite(parsed) ? String(parsed) : "-";
};

const normalizeRiskStatus = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "open") return "Draft";
  if (["monitoring", "review", "in review"].includes(raw)) return "In Review";
  if (raw === "approved") return "Approved";
  if (raw === "closed") return "Closed";
  if (raw === "draft") return "Draft";
  return String(value || "");
};

const buildReviewChangesSummary = ({
  beforeResidualScore,
  afterResidualScore,
  beforeResidualBand,
  afterResidualBand,
  beforeResidualLevels,
  afterResidualLevels,
  likelihoodByLevel,
  impactByLevel,
}) => {
  const lines = [
    `Residual score changed from ${formatReviewScore(beforeResidualScore)} (${formatBandLabel(
      beforeResidualBand
    )}) to ${formatReviewScore(afterResidualScore)} (${formatBandLabel(afterResidualBand)}).`,
  ];

  if (beforeResidualLevels?.likelihood !== afterResidualLevels?.likelihood) {
    lines.push(
      `Likelihood changed from ${formatReviewLevelLabel(
        likelihoodByLevel,
        beforeResidualLevels?.likelihood
      )} to ${formatReviewLevelLabel(likelihoodByLevel, afterResidualLevels?.likelihood)}.`
    );
  }

  if (beforeResidualLevels?.impact !== afterResidualLevels?.impact) {
    lines.push(
      `Impact changed from ${formatReviewLevelLabel(impactByLevel, beforeResidualLevels?.impact)} to ${formatReviewLevelLabel(
        impactByLevel,
        afterResidualLevels?.impact
      )}.`
    );
  }

  return lines.join(" ");
};

const formatHistoryDate = (value) => {
  if (!value) return "Unknown date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Unknown date";
  return parsed.toLocaleString();
};

const formatHistoryListDate = (value) => {
  if (!value) return "Unknown date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Unknown date";
  return parsed.toLocaleString();
};

const parseAssessmentRating = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return { score: null, band: null, text: "-" };
  const full = raw.match(/^(-?\d+(?:\.\d+)?)\s*\(([^)]+)\)$/);
  if (full) {
    return {
      score: Number(full[1]),
      band: String(full[2] || "").trim() || null,
      text: raw,
    };
  }
  const numeric = raw.match(/-?\d+(?:\.\d+)?/);
  return {
    score: numeric ? Number(numeric[0]) : null,
    band: null,
    text: raw,
  };
};

const toChangedAtTime = (value) => {
  const parsed = new Date(value || "").getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const HISTORICAL_POSITION_UNAVAILABLE = "Historical matrix position unavailable";
const REVIEW_OPERATION_MAX_GAP_MS = 60 * 1000;

const getNormalizedHistoryField = (entry) => String(entry?.field || "").trim().toLowerCase();

const isAssessmentEntry = (entry) =>
  getNormalizedHistoryField(entry) === "assessment";

const compareHistoryEntries = (left, right) => {
  const leftTime = toChangedAtTime(left?.changed_at);
  const rightTime = toChangedAtTime(right?.changed_at);
  if (leftTime != null && rightTime != null && leftTime !== rightTime) {
    return leftTime - rightTime;
  }
  if (leftTime == null && rightTime != null) return 1;
  if (leftTime != null && rightTime == null) return -1;
  const leftId = Number(left?.id);
  const rightId = Number(right?.id);
  if (Number.isFinite(leftId) && Number.isFinite(rightId) && leftId !== rightId) {
    return leftId - rightId;
  }
  return 0;
};

const sortHistoryChronological = (entries) =>
  [...(Array.isArray(entries) ? entries : [])].sort(compareHistoryEntries);


const parseNumericHistoryValue = (value) => parseAssessmentRating(value).score;

const SCORING_STATE_FIELD_CONFIG = {
  likelihood: { key: "likelihood", parse: toLevel },
  impact: { key: "impact", parse: toLevel },
  inherent_likelihood: { key: "inherentLikelihood", parse: toLevel },
  inherent_impact: { key: "inherentImpact", parse: toLevel },
  residual_likelihood: { key: "residualLikelihood", parse: toLevel },
  residual_impact: { key: "residualImpact", parse: toLevel },
  inherent_score: { key: "inherentScore", parse: parseNumericHistoryValue },
  residual_score: { key: "residualScore", parse: parseNumericHistoryValue },
};

const isScoringStateEntry = (entry) => Boolean(SCORING_STATE_FIELD_CONFIG[getNormalizedHistoryField(entry)]);

const buildCurrentScoringState = (risk) => ({
  likelihood: toLevel(risk?.likelihood),
  impact: toLevel(risk?.impact),
  inherentLikelihood: toLevel(risk?.inherent_likelihood),
  inherentImpact: toLevel(risk?.inherent_impact),
  residualLikelihood: toLevel(risk?.residual_likelihood),
  residualImpact: toLevel(risk?.residual_impact),
  inherentScore: parseNumericHistoryValue(risk?.inherent_score),
  residualScore: parseNumericHistoryValue(risk?.residual_score),
});

const reverseHistoryEntryInState = (state, entry) => {
  const config = SCORING_STATE_FIELD_CONFIG[getNormalizedHistoryField(entry)];
  if (!config) return;
  state[config.key] = config.parse(entry?.from_value);
};

const buildHistoryBatches = (entries) => {
  const batches = [];

  entries.forEach((entry) => {
    const changedAtTime = toChangedAtTime(entry?.changed_at);
    const key = changedAtTime != null ? `time:${changedAtTime}` : `entry:${entry?.id ?? batches.length}`;
    const previousBatch = batches[batches.length - 1] || null;

    if (!previousBatch || previousBatch.key !== key) {
      batches.push({
        key,
        changedAt: entry?.changed_at || null,
        entries: [entry],
      });
      return;
    }

    previousBatch.entries.push(entry);
  });

  return batches;
};

const buildAssessmentOperationRowsById = (entries) => {
  const assessmentEntries = (entries || []).filter(isAssessmentEntry);
  const rowsByAssessmentId = new Map(
    assessmentEntries.map((entry) => [String(entry?.id), []])
  );

  (entries || []).forEach((entry) => {
    if (isAssessmentEntry(entry)) return;

    const entryTime = toChangedAtTime(entry?.changed_at);
    if (entryTime == null) return;

    let closestAssessment = null;
    let closestGap = Number.POSITIVE_INFINITY;

    assessmentEntries.forEach((assessmentEntry) => {
      const assessmentTime = toChangedAtTime(assessmentEntry?.changed_at);
      if (assessmentTime == null) return;

      const gap = Math.abs(assessmentTime - entryTime);
      if (
        gap < closestGap ||
        (gap === closestGap &&
          closestAssessment &&
          compareHistoryEntries(closestAssessment, assessmentEntry) < 0)
      ) {
        closestAssessment = assessmentEntry;
        closestGap = gap;
      }
    });

    if (!closestAssessment || closestGap > REVIEW_OPERATION_MAX_GAP_MS) {
      return;
    }

    rowsByAssessmentId.get(String(closestAssessment?.id))?.push(entry);
  });

  return rowsByAssessmentId;
};

const findLatestOperationRow = (rows, field) => {
  const fields = Array.isArray(field) ? field : [field];
  const normalizedFields = new Set(
    fields.map((value) => String(value || "").trim().toLowerCase()).filter(Boolean)
  );
  for (let index = (rows || []).length - 1; index >= 0; index -= 1) {
    if (normalizedFields.has(getNormalizedHistoryField(rows[index]))) {
      return rows[index];
    }
  }
  return null;
};

const resolveHistoricalDimensionState = (state, operationRows, mode) => {
  if (mode === "inherent") {
    const rawLikelihood =
      toLevel(findLatestOperationRow(operationRows, ["inherent_likelihood", "likelihood"])?.to_value) ??
      toLevel(state?.inherentLikelihood) ??
      toLevel(state?.likelihood);
    const rawImpact =
      toLevel(findLatestOperationRow(operationRows, ["inherent_impact", "impact"])?.to_value) ??
      toLevel(state?.inherentImpact) ??
      toLevel(state?.impact);
    return {
      rawLikelihood,
      rawImpact,
      likelihood: rawLikelihood,
      impact: rawImpact,
      stateScore:
        parseNumericHistoryValue(findLatestOperationRow(operationRows, "inherent_score")?.to_value) ??
        parseNumericHistoryValue(state?.inherentScore),
    };
  }

  const rawLikelihood =
    toLevel(findLatestOperationRow(operationRows, "residual_likelihood")?.to_value) ??
    toLevel(state?.residualLikelihood);
  const rawImpact =
    toLevel(findLatestOperationRow(operationRows, "residual_impact")?.to_value) ??
    toLevel(state?.residualImpact);
  return {
    rawLikelihood,
    rawImpact,
    likelihood: rawLikelihood,
    impact: rawImpact,
    stateScore:
      parseNumericHistoryValue(findLatestOperationRow(operationRows, "residual_score")?.to_value) ??
      parseNumericHistoryValue(state?.residualScore),
  };
};

const formatSnapshotRatingText = (score, band) => {
  if (score == null) return "-";
  return `${formatReviewScore(score)} (${formatBandLabel(band)})`;
};

const buildDimensionSnapshot = ({
  mode,
  state,
  operationRows,
  matrixConfig,
  assessmentFrom,
  assessmentTo,
  allowStateScoreFallback = false,
}) => {
  const dimensionState = resolveHistoricalDimensionState(state, operationRows, mode);
  const scoreField = mode === "inherent" ? "inherent_score" : "residual_score";
  const scoreRow = findLatestOperationRow(operationRows, scoreField);
  const scoreRowFrom = parseNumericHistoryValue(scoreRow?.from_value);
  const scoreRowTo = parseNumericHistoryValue(scoreRow?.to_value);
  const assessmentScoreFrom = mode === "residual" ? assessmentFrom?.score : null;
  const assessmentScoreTo = mode === "residual" ? assessmentTo?.score : null;
  const assessmentBandFrom = mode === "residual" ? assessmentFrom?.band : null;
  const assessmentBandTo = mode === "residual" ? assessmentTo?.band : null;
  const storedPreviousScore = assessmentScoreFrom ?? scoreRowFrom;
  const storedScore = assessmentScoreTo ?? scoreRowTo ?? null;
  const computedScore =
    dimensionState.likelihood != null && dimensionState.impact != null
      ? scoreForLevels(matrixConfig, dimensionState.likelihood, dimensionState.impact)
      : null;
  const score = storedScore ?? computedScore ?? (allowStateScoreFallback ? dimensionState.stateScore : null);
  const band = assessmentBandTo || bandForScore(matrixConfig, score);
  const previousBand = assessmentBandFrom || bandForScore(matrixConfig, storedPreviousScore);
  const hasCoordinates = dimensionState.likelihood != null && dimensionState.impact != null;
  const hasScoreMismatch =
    hasCoordinates &&
    score != null &&
    computedScore != null &&
    Number(score) !== Number(computedScore);

  return {
    rawLikelihood: dimensionState.rawLikelihood,
    rawImpact: dimensionState.rawImpact,
    likelihood: dimensionState.likelihood,
    impact: dimensionState.impact,
    stateScore: dimensionState.stateScore,
    computedScore,
    score,
    band,
    previousScore: storedPreviousScore,
    previousBand,
    positionAvailable: hasCoordinates,
    positionUnavailableReason: hasCoordinates ? null : HISTORICAL_POSITION_UNAVAILABLE,
    hasScoreMismatch,
    text: formatSnapshotRatingText(score, band),
    previousText: formatSnapshotRatingText(storedPreviousScore, previousBand),
    movement: "remained static",
  };
};

const finalizeAssessmentSnapshots = (snapshots, matrixConfig) => {
  return snapshots.map((snapshot, index) => {
    const previousSnapshot = index > 0 ? snapshots[index - 1] : null;
    const nextSnapshot = {
      ...snapshot,
      inherent: { ...snapshot.inherent },
      residual: { ...snapshot.residual },
    };

    ["inherent", "residual"].forEach((mode) => {
      const currentDimension = nextSnapshot[mode];
      const previousDimension = previousSnapshot?.[mode] || null;
      if (currentDimension.previousScore == null && previousDimension?.score != null) {
        currentDimension.previousScore = previousDimension.score;
      }
      if (!currentDimension.previousBand && currentDimension.previousScore != null) {
        currentDimension.previousBand = bandForScore(matrixConfig, currentDimension.previousScore);
      }
      currentDimension.previousText = formatSnapshotRatingText(
        currentDimension.previousScore,
        currentDimension.previousBand
      );
      currentDimension.movement = classifyMovement(currentDimension.previousScore, currentDimension.score);
    });

    nextSnapshot.residual.displayFromText =
      nextSnapshot.residual.previousScore != null
        ? snapshot.residualStoredFromText !== "-"
          ? snapshot.residualStoredFromText
          : nextSnapshot.residual.previousText
        : snapshot.residualStoredFromText;
    nextSnapshot.residual.displayToText =
      nextSnapshot.residual.score != null
        ? snapshot.residualStoredToText !== "-"
          ? snapshot.residualStoredToText
          : nextSnapshot.residual.text
        : snapshot.residualStoredToText;
    return nextSnapshot;
  });
};

const buildAssessmentSnapshots = (historyEntries, risk, matrixConfig) => {
  const chronologicalEntries = sortHistoryChronological(historyEntries);
  const historyBatches = buildHistoryBatches(chronologicalEntries);
  const operationRowsByAssessmentId = buildAssessmentOperationRowsById(chronologicalEntries);
  const state = buildCurrentScoringState(risk);
  const snapshotsNewestFirst = [];

  [...historyBatches].reverse().forEach((batch) => {
    const assessmentsNewestFirst = (batch?.entries || []).filter(isAssessmentEntry).reverse();

    assessmentsNewestFirst.forEach((entry) => {
      const assessmentFrom = parseAssessmentRating(entry?.from_value);
      const assessmentTo = parseAssessmentRating(entry?.to_value);
      const operationRows = operationRowsByAssessmentId.get(String(entry?.id)) || [];
      snapshotsNewestFirst.push({
        id: entry?.id,
        entry,
        changedAt: entry?.changed_at || null,
        changedBy: entry?.changed_by || null,
        note: richTextToPlainText(entry?.note || ""),
        residualStoredFromText: assessmentFrom.text,
        residualStoredToText: assessmentTo.text,
        inherent: buildDimensionSnapshot({
          mode: "inherent",
          state,
          operationRows,
          matrixConfig,
          assessmentFrom,
          assessmentTo,
        }),
        residual: buildDimensionSnapshot({
          mode: "residual",
          state,
          operationRows,
          matrixConfig,
          assessmentFrom,
          assessmentTo,
        }),
      });
    });

    (batch?.entries || []).forEach((entry) => {
      if (isScoringStateEntry(entry)) {
        reverseHistoryEntryInState(state, entry);
      }
    });
  });

  return finalizeAssessmentSnapshots([...snapshotsNewestFirst].reverse(), matrixConfig);
};

const getSnapshotModeKey = (mode) => (mode === "inherent" ? "inherent" : "residual");

const getSnapshotDimension = (snapshot, mode) => snapshot?.[getSnapshotModeKey(mode)] || null;

const buildCurrentHistoricalSnapshot = (risk, matrixConfig) => {
  const state = buildCurrentScoringState(risk);
  const inherent = buildDimensionSnapshot({
    mode: "inherent",
    state,
    operationRows: [],
    matrixConfig,
    assessmentFrom: null,
    assessmentTo: null,
    allowStateScoreFallback: true,
  });
  const residual = buildDimensionSnapshot({
    mode: "residual",
    state,
    operationRows: [],
    matrixConfig,
    assessmentFrom: null,
    assessmentTo: null,
    allowStateScoreFallback: true,
  });

  return {
    id: "current-latest",
    entry: null,
    changedAt: risk?.last_reviewed_at || null,
    changedBy: risk?.updated_by || null,
    note: "",
    residualStoredFromText: "-",
    residualStoredToText: residual.text,
    inherent,
    residual: {
      ...residual,
      displayFromText: residual.previousText,
      displayToText: residual.text,
    },
  };
};

const resolveReviewTemplate = (payload) => ({
  template_body: ensureRichTextHtml(payload?.template_body || DEFAULT_RISK_REVIEW_TEMPLATE.template_body),
  impact_section_default: ensureRichTextHtml(
    payload?.impact_section_default || DEFAULT_RISK_REVIEW_TEMPLATE.impact_section_default
  ),
  mitigation_section_default: ensureRichTextHtml(
    payload?.mitigation_section_default || DEFAULT_RISK_REVIEW_TEMPLATE.mitigation_section_default
  ),
});

const movementLabel = (movement) => {
  if (movement === "increased") return "Increased";
  if (movement === "decreased") return "Decreased";
  return "Remained static";
};

const classifyMovement = (beforeScore, afterScore) => {
  if (beforeScore == null || afterScore == null) return "remained static";
  if (afterScore > beforeScore) return "increased";
  if (afterScore < beforeScore) return "decreased";
  return "remained static";
};

const composeAssessmentNote = ({ summary, impactToBusiness, mitigationPlan }) => {
  const sanitizedSummary = sanitizeRichTextHtml(summary || "");
  const sanitizedImpact = sanitizeRichTextHtml(impactToBusiness || "");
  const sanitizedMitigation = sanitizeRichTextHtml(mitigationPlan || "");
  const sections = [sanitizedSummary];
  if (!isRichTextEmpty(sanitizedImpact)) {
    sections.push(`<p><strong>Impact to business:</strong></p>${sanitizedImpact}`);
  }
  if (!isRichTextEmpty(sanitizedMitigation)) {
    sections.push(`<p><strong>Remediation / mitigation plan:</strong></p>${sanitizedMitigation}`);
  }
  return sanitizeRichTextHtml(sections.filter(Boolean).join(""));
};

const resolveResidualLevels = (risk, patch) => {
  const hasExplicitResidualLikelihood = toLevel(risk?.residual_likelihood) != null;
  const hasExplicitResidualImpact = toLevel(risk?.residual_impact) != null;

  let nextLikelihood = toLevel(risk?.residual_likelihood) ?? toLevel(risk?.likelihood);
  let nextImpact = toLevel(risk?.residual_impact) ?? toLevel(risk?.impact);

  if (patch?.residual_likelihood != null) {
    nextLikelihood = toLevel(patch.residual_likelihood) ?? nextLikelihood;
  }
  if (patch?.residual_impact != null) {
    nextImpact = toLevel(patch.residual_impact) ?? nextImpact;
  }
  if (!hasExplicitResidualLikelihood && patch?.likelihood != null) {
    nextLikelihood = toLevel(patch.likelihood) ?? nextLikelihood;
  }
  if (!hasExplicitResidualImpact && patch?.impact != null) {
    nextImpact = toLevel(patch.impact) ?? nextImpact;
  }

  return { likelihood: nextLikelihood, impact: nextImpact };
};

const scoreForLevels = (matrixConfig, likelihood, impact) =>
  getCellScore({
    scoringMode: matrixConfig?.scoring_mode,
    cellScores: matrixConfig?.cell_scores,
    lik: likelihood,
    imp: impact,
  });

const bandForScore = (matrixConfig, score) =>
  scoreToBand({ score, bandThresholds: matrixConfig?.band_thresholds });

const getScoringSaveError = (err, fallback) => {
  const message =
    err?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    fallback;
  if (Number(err?.status) === 403) {
    return `${message} You need Admin/Manager permissions to change scoring.`;
  }
  return message;
};

export default function ScoringPanel({
  risk,
  history = [],
  canEditScores,
  onPatchRisk,
  compactLayout = false,
  reviewGuidance = null,
}) {
  const riskId = risk?.id;
  const residualLikelihoodValue = risk?.residual_likelihood;
  const residualImpactValue = risk?.residual_impact;
  const normalizedRiskStatus = useMemo(() => normalizeRiskStatus(risk?.status), [risk?.status]);
  const [viewMode, setViewMode] = useState("current");
  const [mode, setMode] = useState("inherent");
  const [matrixConfig, setMatrixConfig] = useState(() => normalizeMatrixConfig(null));
  const [matrixError, setMatrixError] = useState(null);
  const [themes, setThemes] = useState([]);
  const [themesError, setThemesError] = useState(null);
  const [selectedThemeId, setSelectedThemeId] = useState(null);
  const [impactLevels, setImpactLevels] = useState([]);
  const [impactLevelsError, setImpactLevelsError] = useState(null);
  const [likelihoodLevels, setLikelihoodLevels] = useState([]);
  const [likelihoodLevelsError, setLikelihoodLevelsError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [selectedReviewId, setSelectedReviewId] = useState("latest");
  const [pendingAssessment, setPendingAssessment] = useState(null);
  const [reviewModalError, setReviewModalError] = useState(null);
  const [reviewTemplate, setReviewTemplate] = useState(() => resolveReviewTemplate(DEFAULT_RISK_REVIEW_TEMPLATE));
  const autoPersistThemeRef = useRef("");
  const reviewCompletionEnabled = reviewGuidance?.mode === "editable" && normalizedRiskStatus === "Approved";

  useEffect(() => {
    if (!riskId) return;
    const hasResidual = toLevel(residualLikelihoodValue) && toLevel(residualImpactValue);
    setMode(hasResidual ? "residual" : "inherent");
  }, [riskId, residualLikelihoodValue, residualImpactValue]);

  useEffect(() => {
    let active = true;
    getRiskMatrixConfig()
      .then((res) => {
        if (!active) return;
        setMatrixConfig(normalizeMatrixConfig(res?.data || res));
        setMatrixError(null);
      })
      .catch((err) => {
        if (!active) return;
        setMatrixConfig(normalizeMatrixConfig(null));
        setMatrixError(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Unable to load matrix configuration.");
      });

    getRiskRatingDefinitions()
      .then((res) => {
        if (!active) return;
        const payload = res?.data || res || {};
        setLikelihoodLevels(payload?.likelihood || []);
        setLikelihoodLevelsError(null);
      })
      .catch((err) => {
        if (!active) return;
        setLikelihoodLevels([]);
        setLikelihoodLevelsError(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Unable to load likelihood definitions.");
      });

    listImpactThemes()
      .then((res) => {
        if (!active) return;
        const list = Array.isArray(res?.data || res) ? (res?.data || res) : [];
        setThemes(list);
        setThemesError(null);
      })
      .catch((err) => {
        if (!active) return;
        setThemes([]);
        setThemesError(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Unable to load impact themes.");
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadTemplate = async ({ force = false } = {}) => {
      try {
        const res = await getRiskReviewTemplate({ force });
        if (!active) return;
        setReviewTemplate(resolveReviewTemplate(res?.data || {}));
      } catch (_err) {
        if (!active) return;
        setReviewTemplate(resolveReviewTemplate(DEFAULT_RISK_REVIEW_TEMPLATE));
      }
    };

    const handleTemplateChange = () => {
      loadTemplate({ force: true });
    };

    loadTemplate();
    window.addEventListener("riskReviewTemplateChanged", handleTemplateChange);

    return () => {
      active = false;
      window.removeEventListener("riskReviewTemplateChanged", handleTemplateChange);
    };
  }, []);

  useEffect(() => {
    if (!themes.length || !risk) return;
    const fromRisk = toId(risk.impact_theme_id);
    const hasFromRisk = fromRisk && themes.some((theme) => Number(theme.id) === fromRisk);
    const fallbackTheme = themes.find((theme) => theme.is_default) || themes[0] || null;
    const fallbackId = fallbackTheme ? Number(fallbackTheme.id) : null;
    const resolved = hasFromRisk ? fromRisk : fallbackId;
    setSelectedThemeId((prev) => (prev === resolved ? prev : resolved));

    if (!hasFromRisk && resolved && canEditScores && onPatchRisk && risk.id) {
      const dedupeKey = `${risk.id}:${resolved}`;
      if (autoPersistThemeRef.current === dedupeKey) return;
      autoPersistThemeRef.current = dedupeKey;
      onPatchRisk({ impact_theme_id: resolved }, { optimistic: true }).catch(() => {
        // Keep UI usable even when auto-persist fails.
      });
    }
  }, [themes, risk, canEditScores, onPatchRisk]);

  useEffect(() => {
    if (!selectedThemeId) {
      setImpactLevels([]);
      setImpactLevelsError(null);
      return;
    }
    let active = true;
    getImpactThemeLevels(selectedThemeId)
      .then((res) => {
        if (!active) return;
        const payload = res?.data || res || {};
        setImpactLevels(payload?.levels || []);
        setImpactLevelsError(null);
      })
      .catch((err) => {
        if (!active) return;
        setImpactLevels([]);
        setImpactLevelsError(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Unable to load impact definitions.");
      });
    return () => {
      active = false;
    };
  }, [selectedThemeId]);

  useEffect(() => {
    if (viewMode === "history") {
      setSelectedReviewId("latest");
      setMode("residual");
    }
  }, [viewMode]);

  useEffect(() => {
    setSelectedReviewId("latest");
  }, [risk?.id]);

  const assessmentSnapshotsChronological = useMemo(
    () => buildAssessmentSnapshots(history, risk, matrixConfig),
    [history, matrixConfig, risk]
  );
  const assessmentSnapshotsNewestFirst = useMemo(
    () => [...assessmentSnapshotsChronological].reverse(),
    [assessmentSnapshotsChronological]
  );
  const assessmentHistory = useMemo(
    () => assessmentSnapshotsNewestFirst.map((snapshot) => snapshot.entry).filter(Boolean),
    [assessmentSnapshotsNewestFirst]
  );

  useEffect(() => {
    if (selectedReviewId === "latest") return;
    const exists = assessmentSnapshotsNewestFirst.some((snapshot) => String(snapshot?.id) === String(selectedReviewId));
    if (!exists) {
      setSelectedReviewId("latest");
    }
  }, [assessmentSnapshotsNewestFirst, selectedReviewId]);

  const likelihoodByLevel = useMemo(() => toMapByLevel(likelihoodLevels), [likelihoodLevels]);
  const impactByLevel = useMemo(() => toMapByLevel(impactLevels), [impactLevels]);
  const selectedAssessmentSnapshot = useMemo(() => {
    if (selectedReviewId === "latest") return null;
    return assessmentSnapshotsNewestFirst.find((snapshot) => String(snapshot?.id) === String(selectedReviewId)) || null;
  }, [assessmentSnapshotsNewestFirst, selectedReviewId]);
  const currentAssessmentSnapshot = useMemo(
    () => (selectedReviewId === "latest" ? assessmentSnapshotsNewestFirst[0] || null : null),
    [assessmentSnapshotsNewestFirst, selectedReviewId]
  );
  const currentHistoricalSnapshot = useMemo(
    () => buildCurrentHistoricalSnapshot(risk, matrixConfig),
    [matrixConfig, risk]
  );
  const selectedHistoricalDimension = useMemo(
    () => getSnapshotDimension(selectedAssessmentSnapshot, mode),
    [mode, selectedAssessmentSnapshot]
  );

  const selectedLikelihood = useMemo(() => {
    if (selectedHistoricalDimension) {
      return selectedHistoricalDimension.positionAvailable ? selectedHistoricalDimension.likelihood : null;
    }
    if (mode === "residual") {
      return toLevel(risk?.residual_likelihood) || toLevel(risk?.likelihood);
    }
    return toLevel(risk?.likelihood);
  }, [mode, risk?.likelihood, risk?.residual_likelihood, selectedHistoricalDimension]);

  const selectedImpact = useMemo(() => {
    if (selectedHistoricalDimension) {
      return selectedHistoricalDimension.positionAvailable ? selectedHistoricalDimension.impact : null;
    }
    if (mode === "residual") {
      return toLevel(risk?.residual_impact) || toLevel(risk?.impact);
    }
    return toLevel(risk?.impact);
  }, [mode, risk?.impact, risk?.residual_impact, selectedHistoricalDimension]);

  const selectedScore = useMemo(
    () =>
      getCellScore({
        scoringMode: matrixConfig?.scoring_mode,
        cellScores: matrixConfig?.cell_scores,
        lik: selectedLikelihood,
        imp: selectedImpact,
      }),
    [matrixConfig, selectedLikelihood, selectedImpact]
  );

  const selectedBand = useMemo(
    () => scoreToBand({ score: selectedScore, bandThresholds: matrixConfig?.band_thresholds }),
    [selectedScore, matrixConfig]
  );

  const selectedBandColor = useMemo(
    () => bandToColor({ band: selectedBand, bandColors: matrixConfig?.band_colors }),
    [selectedBand, matrixConfig]
  );

  const selectedThemeName = useMemo(
    () => themes.find((theme) => Number(theme.id) === Number(selectedThemeId))?.name || "General",
    [themes, selectedThemeId]
  );

  const currentAssessmentNote = useMemo(
    () => currentAssessmentSnapshot?.note || "",
    [currentAssessmentSnapshot]
  );

  const historicalAssessment = useMemo(() => {
    if (!selectedAssessmentSnapshot) return null;
    const dimension = getSnapshotDimension(selectedAssessmentSnapshot, mode);
    const isResidualMode = mode === "residual";
    return {
      movement: dimension?.movement || "remained static",
      note: selectedAssessmentSnapshot.note,
      changedAt: selectedAssessmentSnapshot.changedAt,
      changedBy: selectedAssessmentSnapshot.changedBy,
      fromText: isResidualMode
        ? selectedAssessmentSnapshot.residual.displayFromText
        : dimension?.previousText || "-",
      toText: isResidualMode
        ? selectedAssessmentSnapshot.residual.displayToText
        : dimension?.text || "-",
      positionAvailable: Boolean(dimension?.positionAvailable),
      positionUnavailableReason: dimension?.positionUnavailableReason || null,
      hasScoreMismatch: Boolean(dimension?.hasScoreMismatch),
    };
  }, [mode, selectedAssessmentSnapshot]);

  const historySnapshotsNewestFirst = useMemo(() => {
    const latestSnapshot = assessmentSnapshotsNewestFirst[0] || null;
    const latestDimension = getSnapshotDimension(latestSnapshot, mode);
    const currentDimension = getSnapshotDimension(currentHistoricalSnapshot, mode);
    const hasCurrentReviewSnapshot = Boolean(currentHistoricalSnapshot?.changedAt);
    const currentSnapshotTime = toChangedAtTime(currentHistoricalSnapshot?.changedAt);
    const latestSnapshotTime = toChangedAtTime(latestSnapshot?.changedAt);
    const currentMatchesLatestByTime =
      currentSnapshotTime != null && latestSnapshotTime != null && currentSnapshotTime === latestSnapshotTime;
    const currentMatchesLatestByValues =
      latestDimension &&
      currentDimension &&
      latestDimension.likelihood === currentDimension.likelihood &&
      latestDimension.impact === currentDimension.impact &&
      (latestDimension.score == null ||
        currentDimension.score == null ||
        Number(latestDimension.score) === Number(currentDimension.score));

    let visibleSnapshots = assessmentSnapshotsNewestFirst;
    if (hasCurrentReviewSnapshot && (!latestSnapshot || !(currentMatchesLatestByTime || currentMatchesLatestByValues))) {
      const injectedSnapshot = {
        ...currentHistoricalSnapshot,
        inherent: { ...currentHistoricalSnapshot.inherent },
        residual: { ...currentHistoricalSnapshot.residual },
      };
      const injectedDimension = getSnapshotDimension(injectedSnapshot, mode);
      if (latestDimension && injectedDimension) {
        injectedDimension.previousScore = latestDimension.score;
        if (!injectedDimension.previousBand && latestDimension.score != null) {
          injectedDimension.previousBand = bandForScore(matrixConfig, latestDimension.score);
        }
        injectedDimension.previousText = formatSnapshotRatingText(
          injectedDimension.previousScore,
          injectedDimension.previousBand
        );
        injectedDimension.movement = classifyMovement(injectedDimension.previousScore, injectedDimension.score);
      }
      visibleSnapshots = [injectedSnapshot, ...assessmentSnapshotsNewestFirst];
    }

    return visibleSnapshots.slice(0, 5).map((snapshot, index) => ({
      ...snapshot,
      markerOrder: index + 1,
    }));
  }, [assessmentSnapshotsNewestFirst, currentHistoricalSnapshot, matrixConfig, mode]);

  const historyListItems = useMemo(
    () =>
      historySnapshotsNewestFirst.map((snapshot) => {
        const dimension = getSnapshotDimension(snapshot, mode);
        return {
          id: snapshot.id,
          markerOrder: snapshot.markerOrder,
          changedAt: snapshot.changedAt,
          score: dimension?.score ?? null,
          bandLabel: formatBandLabel(dimension?.band),
          likelihood: dimension?.likelihood ?? null,
          impact: dimension?.impact ?? null,
          positionAvailable: Boolean(dimension?.positionAvailable),
          positionUnavailableReason: dimension?.positionUnavailableReason || null,
          movementLabel:
            dimension?.movement === "increased"
              ? "Risk Increased"
              : dimension?.movement === "decreased"
                ? "Risk Decreased"
                : "Risk Stable",
        };
      }),
    [historySnapshotsNewestFirst, mode]
  );

  const matrixHistoryPath = useMemo(
    () =>
      historySnapshotsNewestFirst
        .filter((snapshot) => getSnapshotDimension(snapshot, mode)?.positionAvailable)
        .map((snapshot) => {
          const dimension = getSnapshotDimension(snapshot, mode);
          return {
            id: snapshot.id,
            label: snapshot.changedAt ? formatHistoryDate(snapshot.changedAt) : "Historical review",
            likelihood: toLevel(dimension?.likelihood),
            impact: toLevel(dimension?.impact),
            markerOrder: snapshot.markerOrder,
          };
        }),
    [historySnapshotsNewestFirst, mode]
  );

  const isHistoricalView = Boolean(historicalAssessment);
  const isHistoryMode = viewMode === "history";
  const scoringEditLocked =
    saving || !canEditScores || Boolean(pendingAssessment) || isHistoricalView || isHistoryMode;

  const handleThemeChange = async (event) => {
    if (isHistoricalView || isHistoryMode) return;
    const nextThemeId = Number(event.target.value || 0) || null;
    setSelectedThemeId(nextThemeId);
    if (!nextThemeId || !canEditScores || !onPatchRisk) return;
    setActionError(null);
    setSaving(true);
    try {
      await onPatchRisk({ impact_theme_id: nextThemeId }, { optimistic: true });
    } catch (err) {
      setActionError(getScoringSaveError(err, "Failed to update impact theme."));
    } finally {
      setSaving(false);
    }
  };

  const handleCellSelect = async (likelihood, impact) => {
    if (isHistoryMode) return;
    if (isHistoricalView) {
      setActionError("Switch 'View previous review' back to Latest to edit scoring.");
      return;
    }
    if (!canEditScores || !onPatchRisk) {
      setActionError("You need Admin/Manager permissions to change scoring.");
      return;
    }

    const isSameSelection = selectedLikelihood === likelihood && selectedImpact === impact;
    if (isSameSelection && !reviewCompletionEnabled) return;

    const hasExplicitResidualSelection =
      toLevel(risk?.residual_likelihood) != null && toLevel(risk?.residual_impact) != null;
    const basePatch =
      mode === "residual"
        ? { residual_likelihood: likelihood, residual_impact: impact }
        : {
            likelihood,
            impact,
            // Keep residual scoring in sync with first matrix-based scoring selection
            // so register/heatmap residual views reflect what the user selected.
            ...(!hasExplicitResidualSelection
              ? { residual_likelihood: likelihood, residual_impact: impact }
              : {}),
          };
    const fallbackThemeId = selectedThemeId || toId(themes.find((theme) => theme.is_default)?.id) || toId(themes[0]?.id);
    const patch = fallbackThemeId ? { ...basePatch, impact_theme_id: Number(fallbackThemeId) } : basePatch;

    const beforeResidualLevels = resolveResidualLevels(risk, {});
    const afterResidualLevels = resolveResidualLevels(risk, patch);
    const beforeResidualScore = scoreForLevels(
      matrixConfig,
      beforeResidualLevels.likelihood,
      beforeResidualLevels.impact
    );
    const afterResidualScore = scoreForLevels(
      matrixConfig,
      afterResidualLevels.likelihood,
      afterResidualLevels.impact
    );
    const beforeResidualBand = bandForScore(matrixConfig, beforeResidualScore);
    const afterResidualBand = bandForScore(matrixConfig, afterResidualScore);
    const movement = classifyMovement(beforeResidualScore, afterResidualScore);
    const reviewDate = new Date().toLocaleDateString();
    const summary = replaceTemplatePlaceholders(reviewTemplate?.template_body, {
      review_date: reviewDate,
      change_direction: movementLabel(movement),
      residual_score: afterResidualScore ?? "-",
      band_label: formatBandLabel(afterResidualBand),
      risk_title: String(risk?.name || `Risk #${risk?.id || ""}`).trim(),
      risk_id: String(risk?.id || ""),
      residual_score_from: formatReviewScore(beforeResidualScore),
      residual_score_to: formatReviewScore(afterResidualScore),
      band_label_from: formatBandLabel(beforeResidualBand),
      band_label_to: formatBandLabel(afterResidualBand),
      residual_likelihood_from: formatReviewLevelLabel(likelihoodByLevel, beforeResidualLevels.likelihood),
      residual_likelihood_to: formatReviewLevelLabel(likelihoodByLevel, afterResidualLevels.likelihood),
      residual_impact_from: formatReviewLevelLabel(impactByLevel, beforeResidualLevels.impact),
      residual_impact_to: formatReviewLevelLabel(impactByLevel, afterResidualLevels.impact),
      impact_theme: selectedThemeName,
      review_changes_summary: buildReviewChangesSummary({
        beforeResidualScore,
        afterResidualScore,
        beforeResidualBand,
        afterResidualBand,
        beforeResidualLevels,
        afterResidualLevels,
        likelihoodByLevel,
        impactByLevel,
      }),
    });
    const impactToBusiness =
      reviewTemplate?.impact_section_default ||
      richTextFromPlainText(risk?.impact_implications || "Describe how this change impacts the business.");
    const mitigationPlan =
      reviewTemplate?.mitigation_section_default ||
      richTextFromPlainText(risk?.mitigation || risk?.treatment_plan || "Describe remediation and mitigation actions.");

    setActionError(null);
    setReviewModalError(null);
    setPendingAssessment({
      patch,
      summary,
      impactToBusiness,
      mitigationPlan,
      nextReviewDate: risk?.next_review_date ? new Date(risk.next_review_date).toISOString().slice(0, 10) : "",
      residualScore: afterResidualScore,
      residualBand: formatBandLabel(afterResidualBand),
      movement,
      isReviewCompletion: reviewCompletionEnabled,
    });
  };

  const handlePendingAssessmentChange = (key, value) => {
    setPendingAssessment((current) => (current ? { ...current, [key]: value } : current));
  };

  const handleCancelAssessment = () => {
    setPendingAssessment(null);
    setReviewModalError(null);
  };

  const handleConfirmAssessment = async () => {
    if (!pendingAssessment || !onPatchRisk) return;
    const summary = pendingAssessment.summary || "";
    if (isRichTextEmpty(summary)) {
      setReviewModalError("Review summary is required.");
      return;
    }
    if (pendingAssessment.isReviewCompletion && !pendingAssessment.nextReviewDate) {
      setReviewModalError("Next review date is required.");
      return;
    }

    const assessmentNote = composeAssessmentNote({
      summary,
      impactToBusiness: pendingAssessment.impactToBusiness,
      mitigationPlan: pendingAssessment.mitigationPlan,
    });
    const patch = {
      ...pendingAssessment.patch,
      assessment_note: assessmentNote,
      assessment_impact_to_business: isRichTextEmpty(pendingAssessment.impactToBusiness)
        ? null
        : sanitizeRichTextHtml(pendingAssessment.impactToBusiness),
      assessment_mitigation_plan: isRichTextEmpty(pendingAssessment.mitigationPlan)
        ? null
        : sanitizeRichTextHtml(pendingAssessment.mitigationPlan),
      ...(pendingAssessment.isReviewCompletion
        ? {
            complete_review: true,
            next_review_date: new Date(pendingAssessment.nextReviewDate).toISOString(),
          }
        : {}),
    };

    setReviewModalError(null);
    setActionError(null);
    setSaving(true);
    try {
      await onPatchRisk(patch, { optimistic: true });
      setPendingAssessment(null);
    } catch (err) {
      setActionError(getScoringSaveError(err, "Failed to update scoring."));
    } finally {
      setSaving(false);
    }
  };

  const scoringModeButtons = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing.sm,
        flexWrap: "wrap",
      }}
    >
      {["current", "history"].map((nextView) => {
        const active = viewMode === nextView;
        return (
          <Button
            key={nextView}
            type="button"
            variant="secondary"
            onClick={() => setViewMode(nextView)}
            style={{
              borderRadius: 999,
              border: active ? "1px solid #111827" : "1px solid #d1d5db",
              background: active ? "#111827" : "#ffffff",
              color: active ? "#ffffff" : "#111827",
              padding: `${spacing.xs}px ${spacing.md}px`,
              boxShadow: "none",
            }}
          >
            {nextView === "current" ? "Current" : "History"}
          </Button>
        );
      })}
      {["inherent", "residual"].map((nextMode) => {
        const active = mode === nextMode;
        return (
          <Button
            key={nextMode}
            type="button"
            variant="secondary"
            onClick={() => setMode(nextMode)}
            disabled={viewMode === "history" && nextMode !== "residual"}
            style={{
              borderRadius: 999,
              border: active ? "1px solid #111827" : "1px solid #d1d5db",
              background: active ? "#111827" : "#ffffff",
              color: active ? "#ffffff" : "#111827",
              padding: `${spacing.xs}px ${spacing.md}px`,
              boxShadow: "none",
              opacity: viewMode === "history" && nextMode !== "residual" ? 0.6 : 1,
            }}
          >
            {nextMode === "inherent" ? "Inherent" : "Residual"}
          </Button>
        );
      })}
    </div>
  );

  return (
    <CollapsibleSection title="Risk Scoring" defaultOpen>
      <div style={sectionHeaderStyle}>
        <div style={{ display: "grid", gap: compactLayout ? spacing.sm : 0 }}>
          <p style={{ margin: 0, marginTop: spacing.xs / 2, color: "#6b7280", fontSize: typography.size.xs }}>
            Matrix-first scoring aligned with tenant matrix configuration.
          </p>
          {compactLayout && scoringModeButtons}
        </div>
        {!compactLayout && scoringModeButtons}
      </div>

      {reviewGuidance?.title && reviewGuidance?.body && (
        <div
          role="note"
          aria-label={reviewGuidance.title}
          style={{
            border: `1px solid ${colors.infoBorder}`,
            background: colors.infoSubtle,
            color: colors.info,
            borderRadius: 10,
            padding: spacing.md,
            display: "grid",
            gap: spacing.xs / 2,
          }}
        >
          <div style={{ fontWeight: typography.weight.bold, fontSize: typography.size.sm }}>
            {reviewGuidance.title}
          </div>
          <div style={{ fontSize: typography.size.xs, lineHeight: 1.5 }}>{reviewGuidance.body}</div>
        </div>
      )}

      <div style={controlsRowStyle}>
        <label style={fieldLabelStyle}>
          <span style={{ fontSize: typography.size.xs, color: "#475569" }}>Impact Theme</span>
          <Select
            aria-label="Impact Theme"
            value={selectedThemeId ?? ""}
            onChange={handleThemeChange}
            disabled={scoringEditLocked || !themes.length || viewMode === "history"}
            style={selectStyle}
          >
            <option value="">Select theme</option>
            {themes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.name}
              </option>
            ))}
          </Select>
        </label>
        {viewMode !== "history" && (
          <label style={fieldLabelStyle}>
            <span style={{ fontSize: typography.size.xs, color: "#475569" }}>View previous review</span>
            <Select
              aria-label="View previous review"
              value={selectedReviewId}
              onChange={(event) => {
                setSelectedReviewId(event.target.value);
                setActionError(null);
              }}
              disabled={Boolean(pendingAssessment)}
              style={selectStyle}
            >
              <option value="latest">Latest</option>
              {assessmentHistory.map((entry) => (
                <option key={entry.id} value={String(entry.id)}>
                  {formatHistoryDate(entry.changed_at)}
                </option>
              ))}
            </Select>
          </label>
        )}
        {!canEditScores && (
          <span style={{ color: "#6b7280", fontSize: typography.size.xs }}>
            {reviewGuidance?.mode === "readonly"
              ? "This risk is read-only for your current access level."
              : "Only admins and risk managers can edit scoring."}
          </span>
        )}
        {isHistoricalView && (
          <span style={{ color: "#6b7280", fontSize: typography.size.xs }}>
            Historical review selected. Switch to Latest to edit scoring.
          </span>
        )}
      </div>

      {matrixError && <div style={errorStyle}>{matrixError}</div>}
      {themesError && <div style={errorStyle}>{themesError}</div>}
      {impactLevelsError && <div style={errorStyle}>{impactLevelsError}</div>}
      {likelihoodLevelsError && <div style={errorStyle}>{likelihoodLevelsError}</div>}
      {actionError && <div style={errorStyle}>{actionError}</div>}

      <div
        style={{
          ...matrixLayoutStyle,
          gridTemplateColumns: compactLayout
            ? "minmax(0, 1fr)"
            : matrixLayoutStyle.gridTemplateColumns,
        }}
      >
        <div style={matrixGridWrapStyle}>
          <RiskMatrixSelector
            likelihoodLevels={likelihoodLevels}
            impactLevels={impactLevels}
            selectedLikelihood={selectedLikelihood}
            selectedImpact={selectedImpact}
            onSelect={({ likelihood, impact }) => handleCellSelect(likelihood, impact)}
            impactTheme={{ name: selectedThemeName }}
            readOnly={scoringEditLocked}
            disabled={saving}
            matrixConfig={matrixConfig}
            compact={compactLayout}
            historyPath={viewMode === "history" ? matrixHistoryPath : []}
          />
        </div>

        <div style={detailsPanelStyle}>
          {viewMode === "history" && (
            <>
              <div style={{ fontWeight: typography.weight.medium }}>Last 5 Reviews</div>
              <div style={detailTextStyle}>
                Showing the latest 5 reviews. Numbering starts at latest (1), then prior reviews in order.
              </div>
              {historyListItems.length < 2 && (
                <div style={detailTextStyle}>
                  Not enough historical assessments to display risk movement.
                </div>
              )}
              {historyListItems.length >= 2 && (
                <div style={historyListWrapStyle}>
                  {historyListItems.map((item) => (
                    <div key={`history-list-${item.id}`} style={historyListItemStyle}>
                      <div style={detailLineStyle}>
                        {item.markerOrder}. {formatHistoryListDate(item.changedAt)} - {item.movementLabel} - {item.score ?? "-"} ({item.bandLabel})
                      </div>
                      {!item.positionAvailable && (
                        <div style={detailTextStyle}>{item.positionUnavailableReason || HISTORICAL_POSITION_UNAVAILABLE}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {viewMode !== "history" && !historicalAssessment && (
            <>
              <div style={{ fontWeight: typography.weight.medium }}>Selection Details</div>
              <div style={detailLineStyle}>
                <strong>Likelihood:</strong>{" "}
                {selectedLikelihood
                  ? `L${selectedLikelihood} - ${likelihoodByLevel.get(selectedLikelihood)?.label || "Level"}`
                  : "-"}
              </div>
              <div style={detailTextStyle}>
                {selectedLikelihood
                  ? likelihoodByLevel.get(selectedLikelihood)?.definition || "Definition unavailable."
                  : "Select a matrix cell to view likelihood and impact details."}
              </div>
              <div style={detailLineStyle}>
                <strong>Impact ({selectedThemeName}):</strong>{" "}
                {selectedImpact ? `I${selectedImpact} - ${impactByLevel.get(selectedImpact)?.label || "Level"}` : "-"}
              </div>
              <div style={detailTextStyle}>
                {selectedImpact ? impactByLevel.get(selectedImpact)?.definition || "Definition unavailable." : ""}
              </div>
              <div style={detailLineStyle}>
                <strong>Score:</strong> {selectedScore ?? "-"}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
                <strong style={{ fontSize: typography.size.sm }}>Band:</strong>
                <StatusBadge value={selectedBand} label={selectedBand || "Unrated"} />
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 999,
                    background: selectedBandColor,
                    boxShadow: "0 0 0 1px rgba(15,23,42,0.2)",
                  }}
                />
              </div>
              <div style={{ fontWeight: typography.weight.medium }}>Current Review Note</div>
              <div data-testid="current-review-note" style={historicalNoteStyle}>
                {currentAssessmentNote || "No current review note saved."}
              </div>
            </>
          )}
          {viewMode !== "history" && historicalAssessment && (
            <>
              <div style={{ fontWeight: typography.weight.medium }}>Historical Review</div>
              <div style={detailLineStyle}>
                <strong>Reviewed:</strong> {formatHistoryDate(historicalAssessment.changedAt)}
              </div>
              <div style={detailLineStyle}>
                <strong>Reviewed by:</strong> {historicalAssessment.changedBy || "system"}
              </div>
              <div style={detailLineStyle}>
                <strong>Rating:</strong> {historicalAssessment.toText}
              </div>
              <div style={detailLineStyle}>
                <strong>Movement:</strong> {movementLabel(historicalAssessment.movement)}
              </div>
              <div style={detailTextStyle}>
                Previous: {historicalAssessment.fromText}
              </div>
              {!historicalAssessment.positionAvailable && (
                <div data-testid="historical-position-status" style={detailTextStyle}>
                  {historicalAssessment.positionUnavailableReason || HISTORICAL_POSITION_UNAVAILABLE}
                </div>
              )}
              <div data-testid="historical-review-note" style={historicalNoteStyle}>
                {historicalAssessment.note || "No summary note saved for this review."}
              </div>
            </>
          )}
        </div>
      </div>

      <AssessmentReviewModal
        open={Boolean(pendingAssessment)}
        saving={saving}
        title={pendingAssessment?.isReviewCompletion ? "Review Risk" : "Assessment Review Note"}
        summary={pendingAssessment?.summary || ""}
        impactToBusiness={pendingAssessment?.impactToBusiness || ""}
        mitigationPlan={pendingAssessment?.mitigationPlan || ""}
        nextReviewDate={pendingAssessment?.nextReviewDate || ""}
        residualScore={pendingAssessment?.residualScore}
        residualBand={pendingAssessment?.residualBand}
        movement={pendingAssessment?.movement}
        showReviewDateField={Boolean(pendingAssessment?.isReviewCompletion)}
        error={reviewModalError}
        onSummaryChange={(value) => handlePendingAssessmentChange("summary", value)}
        onImpactToBusinessChange={(value) => handlePendingAssessmentChange("impactToBusiness", value)}
        onMitigationPlanChange={(value) => handlePendingAssessmentChange("mitigationPlan", value)}
        onNextReviewDateChange={(event) => handlePendingAssessmentChange("nextReviewDate", event.target.value)}
        onCancel={handleCancelAssessment}
        confirmLabel={pendingAssessment?.isReviewCompletion ? "Save Review" : "Save Review & Apply Change"}
        onConfirm={handleConfirmAssessment}
      />
    </CollapsibleSection>
  );
}

const sectionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.md,
  flexWrap: "wrap",
};

const controlsRowStyle = {
  display: "flex",
  alignItems: "end",
  gap: spacing.md,
  flexWrap: "wrap",
};

const fieldLabelStyle = {
  display: "grid",
  gap: spacing.xs / 2,
  minWidth: 220,
};

const selectStyle = {
  borderRadius: 8,
  border: "1px solid #d1d5db",
};

const errorStyle = {
  color: "#b91c1c",
  fontSize: typography.size.xs,
};

const matrixLayoutStyle = {
  display: "grid",
  gap: spacing.lg,
  gridTemplateColumns: "minmax(320px, 1fr) minmax(240px, 320px)",
};

const matrixGridWrapStyle = {
  display: "grid",
  gap: spacing.xs,
  minWidth: 0,
};

const detailsPanelStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: spacing.md,
  background: "#ffffff",
  display: "grid",
  gap: spacing.sm,
  alignContent: "start",
};

const detailLineStyle = {
  fontSize: typography.size.sm,
  color: "#111827",
};

const detailTextStyle = {
  fontSize: typography.size.xs,
  color: "#6b7280",
};

const historyListWrapStyle = {
  display: "grid",
  gap: spacing.xs,
};

const historyListItemStyle = {
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  padding: `${spacing.xs}px ${spacing.sm}px`,
  background: "#f8fafc",
};

const historicalNoteStyle = {
  fontSize: typography.size.xs,
  color: "#4b5563",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: spacing.sm,
  whiteSpace: "pre-wrap",
  maxHeight: 180,
  overflowY: "auto",
};
